import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import Seo from '@/components/Seo';
import Plan3D from '@/components/Plan3D';
import { PlanShape2D, ArrowDefs, ScaleBadge } from '@/components/PlanShape2D';
import { renderPlan, renderBeforeAfter } from '@/lib/planRender';
import { PlanDrafts, PlanTemplates, PlanOverlay, PlanOverlayControls, PlanToQuote } from '@/components/PlanTools';
import { getTexture, EDGES, GROUNDS } from '@/lib/planTextures';
import { MATERIALS, CATEGORIES, MATERIAL_BY_NAME, DEFAULT_RATES, shapeArea, aggregateEstimate } from '@/lib/planMaterials';
import { toast, Toaster } from 'sonner';
import { Ruler, Download, Save, FileText, Trash2, Lock, Loader2, Upload, Share2, Copy, CopyPlus, ExternalLink, X, Eye, CheckCircle2, RotateCw, SlidersHorizontal, Spline, PenTool, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const BOARD_W = 720;
const esc = (t) => String(t).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const M2_TO_FT2 = 10.7639;
const M_TO_FT = 3.28084;
const MAT_COLOR = Object.fromEntries(MATERIALS.map((m) => [m.name, m.color]));

const uid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : 'id' + Math.random().toString(36).slice(2));

function dataUrlToBlob(dataUrl) {
  const [meta, b64] = dataUrl.split(',');
  const mime = meta.match(/:(.*?);/)[1];
  const bin = atob(b64); const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

async function fileToDataUrl(file) {
  return new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
}
async function downscaleImage(file, maxDim = 2048, quality = 0.9) {
  let bmp;
  try { bmp = await createImageBitmap(file, { imageOrientation: 'from-image' }); }
  catch { bmp = await createImageBitmap(file); }
  let width = bmp.width, height = bmp.height;
  const scale = Math.min(1, maxDim / Math.max(width, height));
  width = Math.round(width * scale); height = Math.round(height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = width; canvas.height = height;
  canvas.getContext('2d').drawImage(bmp, 0, 0, width, height);
  const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', quality));
  return await fileToDataUrl(blob || file);
}

export default function PlanDesignerPage() {
  const { user } = useAuth();
  const isPro = user?.is_pro || user?.role === 'admin';
  const boardRef = useRef(null);
  const drag = useRef(null);
  const plan3dRef = useRef(null);
  const [plotW, setPlotW] = useState(10);
  const [plotL, setPlotL] = useState(8);
  const [unit, setUnit] = useState('m');
  const [shapes, setShapes] = useState([]);
  const [sel, setSel] = useState(null);
  const [logo, setLogo] = useState(null);
  const [render, setRender] = useState(null);
  const [shareLink, setShareLink] = useState('');
  const [view, setView] = useState('2d');
  const [mine, setMine] = useState([]);
  const [busy, setBusy] = useState('');
  const [snap, setSnap] = useState(true);
  const [gridStep, setGridStep] = useState(0.25);
  const [rates, setRates] = useState(DEFAULT_RATES);
  const [guides, setGuides] = useState([]);
  const [drawing, setDrawing] = useState(false);
  const [drawPts, setDrawPts] = useState([]);
  const [drawMat, setDrawMat] = useState('custom');
  const [ground, setGround] = useState('turf');
  const [realistic, setRealistic] = useState(true);
  const [showDims, setShowDims] = useState(true);
  const [planTitle, setPlanTitle] = useState('');
  const [planClient, setPlanClient] = useState('');
  const [draftId, setDraftId] = useState(null);
  const [overlay, setOverlay] = useState(null);
  const [params, setParams] = useSearchParams();
  const loadState = (d) => {
    setPlanTitle(d.name && d.name !== 'Untitled plan' ? d.name : ''); setPlanClient(d.client || '');
    setPlotW(d.plotW || 10); setPlotL(d.plotL || 8); setUnit(d.unit || 'm'); setGround(d.ground || 'turf');
    setShapes((d.shapes || []).map((s) => ({ ...s, id: uid() }))); setSel(null);
  };
  const getDraftState = () => ({ name: planTitle || 'Untitled plan', client: planClient, plotW, plotL, unit, ground, shapes });
  const onDraftSaved = (id) => { setDraftId(id); setParams(id ? { draft: id } : {}, { replace: true }); };
  useEffect(() => {
    const id = params.get('draft');
    if (id && id !== draftId) api.get(`/plan-drafts/${id}`).then(({ data }) => { loadState(data); setDraftId(id); }).catch(() => toast.error('Saved plan not found'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const applyTemplate = (t) => loadState({ name: planTitle, client: planClient, plotW: t.plotW, plotL: t.plotL, unit, ground: t.ground, shapes: t.shapes });

  useEffect(() => { api.get('/me/logo').then((r) => setLogo(r.data?.data_url || null)).catch(() => {}); }, []);
  useEffect(() => { api.get('/plans/rates').then((r) => { if (r.data && Object.keys(r.data).length) setRates((p) => ({ ...p, ...r.data })); }).catch(() => {}); }, []);
  const snapVal = (v) => (snap ? Math.round(v / gridStep) * gridStep : Math.round(v * 100) / 100);
  const loadMine = () => api.get('/plans/mine').then((r) => setMine(r.data || [])).catch(() => {});
  useEffect(() => { loadMine(); }, []);

  const scale = BOARD_W / plotW;         // px per metre
  const boardH = Math.max(120, plotL * scale);

  const add = (p) => setShapes((s) => [...s, { id: uid(), name: p.name, color: p.color, w: p.w, h: p.h, z: p.z, type: p.type, rot: 0, curve: 0, bow: 0, x: snapVal(Math.max(0, (plotW - p.w) / 2)), y: snapVal(Math.max(0, (plotL - p.h) / 2)) }]);
  const patch = (id, up) => setShapes((s) => s.map((x) => (x.id === id ? { ...x, ...up } : x)));
  const del = (id) => { setShapes((s) => s.filter((x) => x.id !== id)); if (sel === id) setSel(null); };
  const duplicate = (id) => {
    const sh = shapes.find((x) => x.id === id); if (!sh) return;
    const off = snap ? gridStep : 0.4;
    const copy = { ...sh, id: uid(), points: sh.points ? sh.points.map((p) => ({ ...p })) : undefined, x: Math.min(Math.max(0, sh.x + off), Math.max(0, plotW - sh.w)), y: Math.min(Math.max(0, sh.y + off), Math.max(0, plotL - sh.h)) };
    setShapes((s) => [...s, copy]); setSel(copy.id);
    toast.success(`Copied ${sh.name} (${fmtL(sh.w)} × ${fmtL(sh.h)})`);
  };

  const onDown = (e, id, mode) => {
    e.stopPropagation();
    setSel(id);
    const sh = shapes.find((x) => x.id === id);
    drag.current = { id, mode, sx: e.clientX, sy: e.clientY, ox: sh.x, oy: sh.y, ow: sh.w, oh: sh.h, opoints: sh.points, others: shapes.filter((x) => x.id !== id) };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };
  // snap the dragged box edges to other shapes' / plot edges; returns aligned x,y + guide lines
  const alignMove = (nx, ny, w, h, others) => {
    const th = Math.min(0.5, Math.max(0.08, 7 / scale)); const g = [];
    const testX = [[nx, 0], [nx + w / 2, w / 2], [nx + w, w]];
    const targX = [0, plotW / 2, plotW];
    others.forEach((o) => targX.push(o.x, o.x + o.w / 2, o.x + o.w));
    let best = null;
    testX.forEach(([edge, off]) => targX.forEach((t) => { const df = Math.abs(edge - t); if (df < th && (!best || df < best.df)) best = { t, off, df }; }));
    let rx = nx; if (best) { rx = best.t - best.off; g.push({ axis: 'v', pos: best.t }); }
    const testY = [[ny, 0], [ny + h / 2, h / 2], [ny + h, h]];
    const targY = [0, plotL / 2, plotL];
    others.forEach((o) => targY.push(o.y, o.y + o.h / 2, o.y + o.h));
    let bestY = null;
    testY.forEach(([edge, off]) => targY.forEach((t) => { const df = Math.abs(edge - t); if (df < th && (!bestY || df < bestY.df)) bestY = { t, off, df }; }));
    let ry = ny; if (bestY) { ry = bestY.t - bestY.off; g.push({ axis: 'h', pos: bestY.t }); }
    return { x: rx, y: ry, guides: g };
  };
  const onMove = (e) => {
    const d = drag.current; if (!d) return;
    const dxm = (e.clientX - d.sx) / scale, dym = (e.clientY - d.sy) / scale;
    if (d.mode === 'move') {
      let nx = d.ox + dxm, ny = d.oy + dym;
      const al = alignMove(nx, ny, d.ow, d.oh, d.others);
      nx = al.guides.some((q) => q.axis === 'v') ? al.x : snapVal(nx);
      ny = al.guides.some((q) => q.axis === 'h') ? al.y : snapVal(ny);
      nx = Math.min(Math.max(0, nx), plotW - d.ow); ny = Math.min(Math.max(0, ny), plotL - d.oh);
      setGuides(al.guides);
      setShapes((s) => s.map((x) => (x.id === d.id ? { ...x, x: nx, y: ny } : x)));
      return;
    }
    setGuides([]);
    setShapes((s) => s.map((x) => {
      if (x.id !== d.id) return x;
      if (d.mode === 'rotate') {
        const rect = boardRef.current.getBoundingClientRect();
        const cx = rect.left + (x.x + x.w / 2) * scale, cy = rect.top + (x.y + x.h / 2) * scale;
        let ang = Math.atan2(e.clientY - cy, e.clientX - cx) * 180 / Math.PI + 90;
        if (snap) ang = Math.round(ang / 15) * 15;
        return { ...x, rot: Math.round((ang + 360) % 360) };
      }
      const th = ((x.rot || 0) * Math.PI) / 180;
      const lx = dxm * Math.cos(th) + dym * Math.sin(th);
      const ly = -dxm * Math.sin(th) + dym * Math.cos(th);
      const nw = snapVal(Math.max(0.5, Math.min(d.ow + lx, plotW)));
      const nh = snapVal(Math.max(0.5, Math.min(d.oh + ly, plotL)));
      if (x.type === 'poly' && d.opoints) {
        const sx = nw / d.ow, sy = nh / d.oh;
        return { ...x, w: nw, h: nh, points: d.opoints.map((p) => ({ x: p.x * sx, y: p.y * sy })) };
      }
      return { ...x, w: nw, h: nh };
    }));
  };
  const onUp = () => { drag.current = null; setGuides([]); window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp); };

  // freehand drawing
  const boardPoint = (e) => { const r = boardRef.current.getBoundingClientRect(); return { x: (e.clientX - r.left) / scale, y: (e.clientY - r.top) / scale }; };
  const startDraw = () => { setDrawing(true); setDrawPts([]); setSel(null); };
  const cancelDraw = () => { setDrawing(false); setDrawPts([]); };
  const finishDraw = () => {
    if (drawPts.length < 3) { toast.error('Add at least 3 points'); return; }
    const xs = drawPts.map((p) => p.x), ys = drawPts.map((p) => p.y);
    const minX = Math.min(...xs), minY = Math.min(...ys), maxX = Math.max(...xs), maxY = Math.max(...ys);
    const mat = MATERIAL_BY_NAME[MATERIALS.find((m) => m.key === drawMat)?.name] || MATERIAL_BY_NAME['Custom area'];
    const pts = drawPts.map((p) => ({ x: p.x - minX, y: p.y - minY }));
    setShapes((s) => [...s, { id: uid(), name: mat.name, color: mat.color, type: 'poly', points: pts, x: minX, y: minY, w: Math.max(0.5, maxX - minX), h: Math.max(0.5, maxY - minY), z: mat.z, rot: 0 }]);
    setDrawing(false); setDrawPts([]);
    toast.success('Freehand area added');
  };
  const onBoardDown = (e) => {
    if (drawing) { const p = boardPoint(e); setDrawPts((a) => [...a, { x: snapVal(p.x), y: snapVal(p.y) }]); return; }
    setSel(null);
  };

  const totals = useMemo(() => {
    const m = {};
    shapes.forEach((s) => { (m[s.name] ||= { name: s.name, a: 0, shapes: [] }); m[s.name].a += shapeArea(s); m[s.name].shapes.push(s); });
    return Object.values(m).map((g) => ({ name: g.name, a: g.a, lines: aggregateEstimate(g.name, g.shapes, rates) })).sort((x, y) => y.a - x.a);
  }, [shapes, rates]);
  const fmtA = (a) => unit === 'm' ? `${a.toFixed(1)} m²` : `${(a * M2_TO_FT2).toFixed(1)} ft²`;
  const fmtL = (v) => unit === 'm' ? `${v.toFixed(1)}m` : `${(v * M_TO_FT).toFixed(1)}ft`;

  const renderCanvas = () => renderPlan({ shapes, plotW, plotL, boardW: BOARD_W, ground, realistic, showDims, fmtL, fmtA, overlay,
    meta: { title: planTitle, client: planClient, brand: user?.business_name && !/tradesmate/i.test(user.business_name) ? `${user.business_name.toUpperCase()} · DRAWN WITH TRADESMATE` : undefined } });

  // 3D snapshot (PNG data URL) from the live three.js scene
  const getIso = () => plan3dRef.current?.getSnapshot?.() || null;

  const downloadBeforeAfter = async () => {
    const a = document.createElement('a');
    a.href = await renderBeforeAfter({ shapes, plotW, plotL, boardW: BOARD_W, ground, realistic, showDims, fmtL, fmtA, overlay, meta: {} });
    a.download = 'garden-before-after.jpg'; document.body.appendChild(a); a.click(); a.remove();
  };
  const downloadPng = () => {
    const a = document.createElement('a'); a.href = renderCanvas(); a.download = 'garden-plan.png';
    document.body.appendChild(a); a.click(); a.remove();
  };
  const saveGallery = async () => {
    setBusy('save');
    try {
      const fd = new FormData();
      fd.append('file', dataUrlToBlob(renderCanvas()), 'plan.png');
      fd.append('caption', `${planTitle || 'Design plan'}${planClient ? ` (${planClient})` : ''} — ${totals.map((t) => `${t.name} ${fmtA(t.a)}`).join(', ')}`);
      fd.append('kind', 'plan');
      await api.post('/gallery', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Plan saved to your gallery');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); }
    finally { setBusy(''); }
  };

  const onPickRender = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) { toast.error('Please choose an image'); return; }
    try { setRender(await downscaleImage(f)); toast.success('AI render attached'); }
    catch { toast.error('Could not read that image — try another'); }
    e.target.value = '';
  };

  const sharePlan = async () => {
    if (shapes.length === 0) { toast.error('Add at least one area first'); return; }
    setBusy('share');
    try {
      const fd = new FormData();
      fd.append('plan_png', dataUrlToBlob(renderCanvas()), 'plan.png');
      if (render) fd.append('render', dataUrlToBlob(render), 'render.png');
      const iso = getIso();
      if (iso) fd.append('iso', dataUrlToBlob(iso), 'iso.png');
      fd.append('title', [planTitle || (user?.business_name ? `${user.business_name} — design plan` : 'Design plan'), planClient].filter(Boolean).join(' · '));
      fd.append('meta', JSON.stringify({ plotW, plotL, unit, totals, totalArea: totals.reduce((s, t) => s + t.a, 0) }));
      const { data } = await api.post('/plans/share', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const link = `${window.location.origin}/p/${data.token}`;
      setShareLink(link);
      loadMine();
      try { await navigator.clipboard.writeText(link); toast.success('Share link copied'); } catch { toast.success('Share link ready'); }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create link'); }
    finally { setBusy(''); }
  };

  const exportPdf = async () => {
    if (shapes.length === 0) { toast.error('Add at least one area first'); return; }
    setBusy('pdf');
    try {
      const planImg = renderCanvas();
      const isoImg = getIso();
      const rows = totals.map((t) => {
        const lines = (t.lines || []).map((l) => `${l.name} ${l.text}`).join(' · ');
        return `<tr style="page-break-inside:avoid"><td style="padding:8px 10px;border-bottom:1px solid #e2e8f0;vertical-align:top"><div style="font-weight:700">${t.name}</div>${lines ? `<div style="font-size:11px;color:#64748b;margin-top:2px">${lines}</div>` : ''}</td><td style="padding:8px 10px;border-bottom:1px solid #e2e8f0;text-align:right;font-weight:700;white-space:nowrap">${fmtA(t.a)}</td></tr>`;
      }).join('');
      const el = document.createElement('div');
      el.style.width = '760px'; el.style.padding = '24px'; el.style.fontFamily = 'Arial, sans-serif'; el.style.color = '#0f172a';
      const imgWrap = 'style="page-break-inside:avoid;break-inside:avoid;margin-bottom:12px"';
      el.innerHTML = `
        <div style="display:flex;align-items:center;gap:14px;border-bottom:3px solid ${user?.brand_color || '#C2532A'};padding-bottom:12px;margin-bottom:16px">
          ${logo ? `<img src="${logo}" style="height:56px;width:auto"/>` : ''}
          <div><div style="font-size:22px;font-weight:900;text-transform:uppercase">${(user?.business_name || 'Design Plan')}</div>
          <div style="font-size:12px;color:#64748b;letter-spacing:2px;text-transform:uppercase">${esc(planTitle || 'Garden / Driveway Design Plan')}${planClient ? ` · ${esc(planClient)}` : ''}</div></div>
        </div>
        <div style="font-size:12px;color:#64748b;margin-bottom:8px">Plot: ${fmtL(plotW)} × ${fmtL(plotL)} — total ${fmtA(plotW * plotL)}</div>
        <div ${imgWrap}><h3 style="font-size:14px;text-transform:uppercase;letter-spacing:1px;margin:0 0 6px">Plan</h3><img src="${planImg}" style="width:100%;border:2px solid #0f172a;display:block"/></div>
        ${isoImg ? `<div ${imgWrap}><h3 style="font-size:14px;text-transform:uppercase;letter-spacing:1px;margin:0 0 6px">3D preview</h3><img src="${isoImg}" style="width:100%;border:2px solid #0f172a;display:block"/></div>` : ''}
        ${render ? `<div ${imgWrap}><h3 style="font-size:14px;text-transform:uppercase;letter-spacing:1px;margin:0 0 6px">AI concept render</h3><img src="${render}" style="width:100%;border:2px solid #0f172a;display:block"/></div>` : ''}
        <div style="page-break-inside:avoid">
        <h3 style="font-size:14px;text-transform:uppercase;letter-spacing:1px;margin:18px 0 6px">Materials &amp; estimated quantities</h3>
        <table style="width:100%;border-collapse:collapse;font-size:14px"><tbody>${rows}
          <tr><td style="padding:8px 10px;font-weight:900">Total covered</td><td style="padding:8px 10px;text-align:right;font-weight:900">${fmtA(totals.reduce((s, t) => s + t.a, 0))}</td></tr>
        </tbody></table></div>
        <p style="font-size:11px;color:#94a3b8;margin-top:18px">Quantities are indicative estimates — confirm on site. Plan for discussion only. Created with TradesMate.</p>`;
      const html2pdf = (await import('html2pdf.js')).default;
      await html2pdf().set({ margin: 8, filename: 'design-plan.pdf', image: { type: 'jpeg', quality: 0.95 }, html2canvas: { scale: 2, useCORS: true }, jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }, pagebreak: { mode: ['css', 'legacy'] } }).from(el).save();
      toast.success('Branded PDF downloaded');
    } catch (e) { toast.error('Could not create PDF'); }
    finally { setBusy(''); }
  };

  if (!isPro) {
    return (
      <div className="max-w-lg mx-auto px-4 py-20 text-center" data-testid="plan-locked">
        <Lock className="mx-auto text-brand mb-4" size={40} />
        <h1 className="font-display font-semibold text-3xl uppercase tracking-tight">A Pro feature</h1>
        <p className="text-slate-600 mt-2">Sketch accurate to-scale garden &amp; driveway plans with instant material quantities. Start your 14-day free trial to unlock it.</p>
        <a href="/pricing" className="tm-btn inline-flex mt-5" data-testid="plan-upgrade-btn">See Pro plans</a>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-28" data-testid="plan-designer-page">
      <Seo title="Plan Designer" noindex />
      <Toaster position="top-center" richColors />
      <header className="mb-5">
        <p className="text-xs uppercase tracking-widest text-brand font-semibold">Business · Design</p>
        <h1 className="font-display font-semibold text-3xl sm:text-4xl uppercase tracking-tight flex items-center gap-2"><Ruler className="text-brand" size={30} /> Plan Designer</h1>
        <p className="text-slate-600 mt-2">Sketch a to-scale garden or driveway, get instant material quantities, and export a branded PDF for your client.</p>
        <div className="flex flex-wrap items-center gap-2 mt-4" data-testid="plan-file-bar">
          <PlanDrafts draftId={draftId} getState={getDraftState} onLoad={(d) => { loadState(d); onDraftSaved(d.id); }} onSaved={onDraftSaved} />
          <PlanTemplates onApply={applyTemplate} hasShapes={shapes.length > 0} />
          <PlanOverlay overlay={overlay} setOverlay={setOverlay} />
          {draftId && <span className="text-xs text-slate-500" data-testid="plan-draft-status">Editing saved plan</span>}
        </div>
      </header>

      <div className="grid lg:grid-cols-[1fr_300px] gap-6">
        <div className="min-w-0">
          {/* plot + unit */}
          <div className="flex flex-wrap items-end gap-3 mb-3">
            <label className="text-sm">Drawing title<input value={planTitle} onChange={(e) => setPlanTitle(e.target.value)} placeholder="Rear garden redesign" data-testid="plan-title-input" className="block w-48 border border-slate-300 px-2 py-1.5 mt-1 focus:border-brand outline-none" /></label>
            <label className="text-sm">Client / site<input value={planClient} onChange={(e) => setPlanClient(e.target.value)} placeholder="Mr & Mrs Smith, Leeds" data-testid="plan-client-input" className="block w-48 border border-slate-300 px-2 py-1.5 mt-1 focus:border-brand outline-none" /></label>
            <label className="text-sm">Plot width<input type="number" min="1" value={plotW} onChange={(e) => setPlotW(Math.max(1, +e.target.value || 1))} data-testid="plot-w" className="block w-24 border border-slate-300 px-2 py-1.5 mt-1 focus:border-brand outline-none" /></label>
            <label className="text-sm">Plot length<input type="number" min="1" value={plotL} onChange={(e) => setPlotL(Math.max(1, +e.target.value || 1))} data-testid="plot-l" className="block w-24 border border-slate-300 px-2 py-1.5 mt-1 focus:border-brand outline-none" /></label>
            <div className="flex border border-slate-300">
              <button onClick={() => setUnit('m')} data-testid="unit-m" className={`px-3 py-1.5 text-sm font-mono ${unit === 'm' ? 'bg-slate-900 text-white' : ''}`}>m</button>
              <button onClick={() => setUnit('ft')} data-testid="unit-ft" className={`px-3 py-1.5 text-sm font-mono ${unit === 'ft' ? 'bg-slate-900 text-white' : ''}`}>ft</button>
            </div>
            <div className="flex border border-slate-300 ml-auto">
              <button onClick={() => setView('2d')} data-testid="view-2d" className={`px-3 py-1.5 text-sm font-mono uppercase ${view === '2d' ? 'bg-brand text-white' : ''}`}>2D plan</button>
              <button onClick={() => setView('3d')} data-testid="view-3d" className={`px-3 py-1.5 text-sm font-mono uppercase ${view === '3d' ? 'bg-brand text-white' : ''}`}>3D view</button>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <button onClick={() => setSnap((v) => !v)} data-testid="snap-toggle" className={`px-3 py-1.5 text-xs font-mono uppercase border ${snap ? 'bg-slate-900 text-white border-slate-800' : 'border-slate-300'}`}>Snap {snap ? 'on' : 'off'}</button>
            <label className="text-xs flex items-center gap-1.5">Grid
              <select value={gridStep} onChange={(e) => setGridStep(+e.target.value)} data-testid="grid-step" className="border border-slate-300 px-2 py-1 text-sm">
                <option value={0.1}>0.1 m</option><option value={0.25}>0.25 m</option><option value={0.5}>0.5 m</option><option value={1}>1 m</option>
              </select>
            </label>
            <label className="text-xs flex items-center gap-1.5">Ground
              <select value={ground} onChange={(e) => setGround(e.target.value)} data-testid="ground-select" className="border border-slate-300 px-2 py-1 text-sm">
                {GROUNDS.map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}
              </select>
            </label>
            <button onClick={() => setRealistic((v) => !v)} data-testid="realistic-toggle" className={`rounded-full px-3 py-1.5 text-xs font-semibold border ${realistic ? 'bg-slate-900 text-white border-slate-800' : 'border-slate-300'}`}>Textures {realistic ? 'on' : 'off'}</button>
            <button onClick={() => setShowDims((v) => !v)} data-testid="dims-toggle" className={`rounded-full px-3 py-1.5 text-xs font-semibold border ${showDims ? 'bg-slate-900 text-white border-slate-800' : 'border-slate-300'}`}>Measurements {showDims ? 'on' : 'off'}</button>
            <Link to="/settings?tab=rates" data-testid="rates-open" className="rounded-full px-3 py-1.5 text-xs uppercase border border-slate-300 hover:border-brand inline-flex items-center gap-1.5 ml-auto font-semibold"><SlidersHorizontal size={13} /> Estimate rates</Link>
          </div>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {!drawing ? (
              <>
                <button onClick={startDraw} data-testid="draw-start" className="rounded-full px-3 py-1.5 text-xs uppercase border border-slate-200 hover:bg-brand hover:text-white hover:border-brand inline-flex items-center gap-1.5 font-semibold"><PenTool size={13} /> Draw freehand area</button>
                <span className="text-xs text-slate-500">as
                  <select value={drawMat} onChange={(e) => setDrawMat(e.target.value)} data-testid="draw-material" className="ml-1 border border-slate-300 px-1.5 py-1 text-xs">
                    {MATERIALS.filter((m) => m.type !== 'circle' && !m.structure).map((m) => <option key={m.key} value={m.key}>{m.name}</option>)}
                  </select>
                </span>
              </>
            ) : (
              <>
                <span className="text-xs font-bold text-brand" data-testid="draw-hint"><PenTool size={13} className="inline" /> Click to drop points ({drawPts.length}) — then Finish</span>
                <button onClick={finishDraw} data-testid="draw-finish" className="rounded-full px-3 py-1.5 text-xs uppercase border border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white inline-flex items-center gap-1.5 font-semibold"><Check size={13} /> Finish</button>
                <button onClick={() => setDrawPts((a) => a.slice(0, -1))} data-testid="draw-undo" className="px-3 py-1.5 text-xs uppercase border border-slate-300 hover:border-slate-200 font-semibold">Undo point</button>
                <button onClick={cancelDraw} data-testid="draw-cancel" className="px-3 py-1.5 text-xs uppercase border border-slate-300 text-rose-600 hover:border-rose-400 font-semibold">Cancel</button>
              </>
            )}
          </div>
          {/* board / 3D view */}
          <div className="relative">
            {/* 3D is always mounted (offscreen in 2D) so snapshots work in either view */}
            <div aria-hidden={view !== '3d'} style={view === '3d' ? {} : { position: 'absolute', left: -100000, top: 0, width: 760, pointerEvents: 'none' }}>
              <div className="tm-card p-2 bg-slate-100" data-testid="plan-iso">
                <div className="grid sm:grid-cols-[1fr_160px] gap-2">
                  <Plan3D ref={plan3dRef} shapes={shapes} plotW={plotW} plotL={plotL} ground={realistic ? ground : 'plain'} />
                  <div className="text-xs space-y-1 py-1" data-testid="plan-legend">
                    <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 font-semibold">Legend</p>
                    {totals.length === 0
                      ? <p className="text-slate-400">Add areas to build the 3D view.</p>
                      : totals.map((t) => (
                          <div key={t.name} className="flex items-center gap-1.5">
                            <span className="w-3 h-3 shrink-0 border border-slate-300" style={{ background: MAT_COLOR[t.name] }} />
                            <span className="truncate">{t.name}</span>
                          </div>
                        ))}
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 mt-1">Drag to rotate · scroll to zoom · right-drag to pan.</p>
              </div>
            </div>
            {view === '2d' && (
              <div className="overflow-auto tm-card p-2 bg-slate-100">
                <div ref={boardRef} onPointerDown={onBoardDown} data-testid="plan-board" data-ground={realistic ? ground : 'plain'} className={`relative bg-white border border-slate-300 mx-auto shadow-inner ${drawing ? 'cursor-crosshair' : ''}`}
                  style={{ width: BOARD_W, height: boardH, ...(realistic && ground !== 'plain'
                    ? { backgroundImage: `url(${getTexture(ground).url})`, backgroundSize: `${getTexture(ground).tileM * scale}px` }
                    : { backgroundImage: 'linear-gradient(#e2e8f0 1px,transparent 1px),linear-gradient(90deg,#e2e8f0 1px,transparent 1px)', backgroundSize: `${scale}px ${scale}px` }) }}>
                  <ArrowDefs />
                  <ScaleBadge scale={scale} light={realistic && ground !== 'plain'} />
                  {overlay && (
                    <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }}>
                      <img src={overlay.url} alt="Site photo overlay" data-testid="plan-overlay-img" className="w-full h-full object-cover" style={{ transform: `translate(${overlay.dx}%, ${overlay.dy}%) scale(${overlay.zoom})` }} />
                    </div>
                  )}
                  {overlay?.split > 0 && (
                    <div className="absolute inset-0 overflow-hidden pointer-events-none z-40" data-testid="plan-before-layer" style={{ clipPath: `inset(0 ${100 - overlay.split}% 0 0)` }}>
                      <img src={overlay.url} alt="Before" className="w-full h-full object-cover" style={{ transform: `translate(${overlay.dx}%, ${overlay.dy}%) scale(${overlay.zoom})` }} />
                    </div>
                  )}
                  {overlay?.split > 0 && overlay.split < 100 && <div className="absolute top-0 bottom-0 w-0.5 bg-white shadow z-40 pointer-events-none" style={{ left: `${overlay.split}%` }} data-testid="plan-ba-divider" />}
                  {overlay?.split > 0 && <span className="absolute top-2 left-2 z-40 bg-slate-900/85 text-white text-[11px] font-bold tracking-widest px-2 py-1 pointer-events-none">BEFORE</span>}
                  {overlay && overlay.split < 100 && <span className="absolute top-2 right-2 z-40 bg-slate-900/85 text-white text-[11px] font-bold tracking-widest px-2 py-1 pointer-events-none">AFTER</span>}
                  {overlay && <style>{`[data-testid=plan-board] > [data-testid^=shape-]{opacity:${overlay.opacity} !important}`}</style>}
                  {guides.map((g, i) => (g.axis === 'v'
                    ? <div key={i} data-testid="align-guide-v" className="absolute top-0 bottom-0 w-px bg-brand pointer-events-none z-30" style={{ left: g.pos * scale }} />
                    : <div key={i} data-testid="align-guide-h" className="absolute left-0 right-0 h-px bg-brand pointer-events-none z-30" style={{ top: g.pos * scale }} />))}
                  {drawing && drawPts.length > 0 && (
                    <svg width={BOARD_W} height={boardH} className="absolute inset-0 pointer-events-none z-30">
                      <polyline points={drawPts.map((p) => `${p.x * scale},${p.y * scale}`).join(' ')} fill="rgb(var(--tm-brand-rgb) / 0.15)" stroke="var(--tm-orange)" strokeWidth="2" />
                      {drawPts.map((p, i) => <circle key={i} cx={p.x * scale} cy={p.y * scale} r="4" fill="var(--tm-orange)" />)}
                    </svg>
                  )}
                  {shapes.map((s) => (
                    <PlanShape2D key={s.id} s={s} scale={scale} selected={sel === s.id} drawing={drawing} realistic={realistic}
                      showDims={showDims} fmtA={fmtA} fmtL={fmtL} onDown={onDown} />
                  ))}
                </div>
              </div>
            )}
          </div>
          {overlay && view === '2d' && <PlanOverlayControls overlay={overlay} setOverlay={setOverlay} onDownload={downloadBeforeAfter} />}
          <div className="flex flex-wrap gap-2 mt-3">
            <PlanToQuote totals={totals} shapes={shapes} title={planTitle} client={planClient} fmtA={fmtA} />
            <button onClick={downloadPng} data-testid="plan-png-btn" className="tm-btn inline-flex text-sm"><Download size={15} /> PNG</button>
            <button onClick={saveGallery} disabled={busy === 'save'} data-testid="plan-save-btn" className="border border-slate-800 px-3 py-2 text-sm uppercase inline-flex items-center gap-1 hover:bg-slate-900 hover:text-white disabled:opacity-60 font-semibold">{busy === 'save' ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Gallery</button>
            <button onClick={exportPdf} disabled={busy === 'pdf'} data-testid="plan-pdf-btn" className="border border-slate-200 px-3 py-2 text-sm uppercase inline-flex items-center gap-1 hover:bg-brand hover:text-white hover:border-brand disabled:opacity-60 font-semibold">{busy === 'pdf' ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />} Branded PDF</button>
            <button onClick={sharePlan} disabled={busy === 'share'} data-testid="plan-share-btn" className="border border-slate-800 px-3 py-2 text-sm uppercase inline-flex items-center gap-1 hover:bg-slate-900 hover:text-white disabled:opacity-60 font-semibold">{busy === 'share' ? <Loader2 size={14} className="animate-spin" /> : <Share2 size={14} />} Share link</button>
          </div>
          {shareLink && (
            <div className="tm-card p-3 mt-3 flex items-center gap-2 flex-wrap" data-testid="plan-share-box">
              <input readOnly value={shareLink} data-testid="plan-share-link" className="flex-1 min-w-[200px] border border-slate-200 px-2 py-1.5 text-sm font-mono bg-slate-50" />
              <button onClick={() => { navigator.clipboard.writeText(shareLink); toast.success('Copied'); }} data-testid="plan-share-copy" className="border border-slate-800 px-2.5 py-1.5 text-xs uppercase inline-flex items-center gap-1 hover:bg-slate-900 hover:text-white font-semibold"><Copy size={13} /> Copy</button>
              <a href={shareLink} target="_blank" rel="noopener noreferrer" data-testid="plan-share-open" className="border border-slate-200 px-2.5 py-1.5 text-xs uppercase inline-flex items-center gap-1 hover:bg-brand hover:text-white hover:border-brand font-semibold"><ExternalLink size={13} /> Open</a>
            </div>
          )}
        </div>

        {/* sidebar */}
        <div className="space-y-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 mb-2 font-semibold">Add area</p>
            <div className="max-h-[360px] overflow-auto pr-1 space-y-3" data-testid="plan-palette">
              {CATEGORIES.map((cat) => (
                <div key={cat}>
                  <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-1 font-semibold">{cat}</p>
                  <div className="grid grid-cols-2 gap-1.5">
                    {MATERIALS.filter((m) => m.category === cat).map((p) => (
                      <button key={p.key} onClick={() => add(p)} data-testid={`add-${p.key}`} title={p.name}
                        className="flex items-center gap-1.5 border border-slate-200 px-2 py-1.5 text-xs hover:border-brand text-left">
                        <span className="w-5 h-5 shrink-0 rounded-sm border border-black/10" style={{ backgroundImage: `url(${getTexture(p.tex, p.color).url})`, backgroundSize: '40px', borderRadius: p.type === 'circle' ? '50%' : undefined }} />
                        <span className="truncate">{p.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 mb-2 font-semibold">AI render (optional)</p>
            {render ? (
              <div className="tm-card p-2" data-testid="plan-render-preview">
                <img src={render} alt="AI render" className="w-full aspect-[4/3] object-cover bg-slate-100" />
                <div className="flex gap-2 mt-2">
                  <label className="flex-1 text-center cursor-pointer border border-slate-300 py-1.5 text-xs uppercase hover:border-brand font-semibold">Change<input type="file" accept="image/*" className="hidden" onChange={onPickRender} data-testid="plan-render-input" /></label>
                  <button onClick={() => setRender(null)} data-testid="plan-render-remove" className="border border-slate-300 px-2 py-1.5 text-rose-600 hover:border-rose-400"><X size={14} /></button>
                </div>
              </div>
            ) : (
              <label className="tm-card p-4 text-center cursor-pointer block hover:shadow-lg transition-shadow" data-testid="plan-render-drop">
                <Upload className="mx-auto text-brand" size={20} />
                <p className="text-xs font-bold mt-1">Attach AI render</p>
                <p className="text-[11px] text-slate-500">Add the "after" image from the AI Design Visualiser to include it in the PDF &amp; share link.</p>
                <input type="file" accept="image/*" className="hidden" onChange={onPickRender} data-testid="plan-render-input" />
              </label>
            )}
          </div>
          {sel && (() => { const s = shapes.find((x) => x.id === sel); if (!s) return null; return (
            <div className="tm-card p-3 space-y-2" data-testid="plan-selected">
              <div className="flex items-center justify-between"><span className="font-bold text-sm">{s.name}</span><div className="flex items-center gap-2"><button onClick={() => duplicate(s.id)} data-testid="shape-duplicate" title="Duplicate (same size)" className="text-slate-600 hover:text-brand inline-flex items-center gap-1 text-xs uppercase font-semibold"><CopyPlus size={15} /> Copy</button><button onClick={() => del(s.id)} data-testid="shape-delete" title="Delete" className="text-rose-600"><Trash2 size={15} /></button></div></div>
              <div className="flex gap-2">
                <label className="text-xs flex-1">W ({unit})<input type="number" step="0.1" value={(unit === 'm' ? s.w : s.w * M_TO_FT).toFixed(1)} onChange={(e) => patch(s.id, { w: Math.max(0.5, (+e.target.value || 0) / (unit === 'm' ? 1 : M_TO_FT)) })} disabled={s.type === 'poly'} className="block w-full border border-slate-300 px-2 py-1 mt-0.5 disabled:bg-slate-100" /></label>
                <label className="text-xs flex-1">{s.type === 'circle' ? 'H' : 'L'} ({unit})<input type="number" step="0.1" value={(unit === 'm' ? s.h : s.h * M_TO_FT).toFixed(1)} onChange={(e) => patch(s.id, { h: Math.max(0.5, (+e.target.value || 0) / (unit === 'm' ? 1 : M_TO_FT)) })} disabled={s.type === 'poly'} className="block w-full border border-slate-300 px-2 py-1 mt-0.5 disabled:bg-slate-100" /></label>
              </div>
              {s.type === 'poly' && <p className="text-[11px] text-slate-400">Freehand area — drag the orange corner to scale.</p>}
              <div className="text-xs">
                <div className="flex items-center justify-between"><span>Rotation</span><span className="font-mono">{s.rot || 0}°</span></div>
                <div className="flex items-center gap-2 mt-0.5">
                  <input type="range" min="0" max="359" value={s.rot || 0} onChange={(e) => patch(s.id, { rot: +e.target.value })} data-testid="rot-slider" className="flex-1 accent-brand" />
                  <button onClick={() => patch(s.id, { rot: ((s.rot || 0) + 90) % 360 })} data-testid="rot-90" title="Turn 90°" className="border border-slate-300 px-1.5 py-1 hover:border-brand"><RotateCw size={13} /></button>
                </div>
              </div>
              {!MATERIAL_BY_NAME[s.name]?.wall && !MATERIAL_BY_NAME[s.name]?.structure && (
                <label className="text-xs block">Edge border
                  <select value={s.edge || 'none'} onChange={(e) => patch(s.id, { edge: e.target.value })} data-testid="edge-select" className="block w-full border border-slate-300 px-2 py-1 mt-0.5 text-sm">
                    {EDGES.map((ed) => <option key={ed.id} value={ed.id}>{ed.label}</option>)}
                  </select>
                </label>
              )}
              {MATERIAL_BY_NAME[s.name]?.canBow ? (
                <label className="text-xs block" data-testid="bow-control"><span className="flex items-center gap-1"><Spline size={12} /> Curve / bow</span>
                  <input type="range" min="-1" max="1" step="0.05" value={s.bow || 0} onChange={(e) => patch(s.id, { bow: +e.target.value })} data-testid="bow-slider" className="w-full accent-brand mt-0.5" /></label>
              ) : s.type !== 'circle' && s.type !== 'poly' && !MATERIAL_BY_NAME[s.name]?.structure ? (
                <label className="text-xs block" data-testid="curve-control"><span className="flex items-center gap-1"><Spline size={12} /> Rounded corners</span>
                  <input type="range" min="0" max="1" step="0.05" value={s.curve || 0} onChange={(e) => patch(s.id, { curve: +e.target.value })} data-testid="curve-slider" className="w-full accent-brand mt-0.5" /></label>
              ) : null}
            </div>
          ); })()}
          <div className="tm-card p-3" data-testid="plan-totals">
            <p className="text-xs uppercase tracking-widest text-slate-500 mb-2 font-semibold">Materials &amp; quantities</p>
            {totals.length === 0 ? <p className="text-sm text-slate-400">Add areas to see quantities.</p> : (
              <div className="space-y-2.5">
                {totals.map((t) => (
                  <div key={t.name} data-testid={`mat-${t.name}`}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-bold">{t.name}</span>
                      <span className="font-mono font-bold">{fmtA(t.a)}</span>
                    </div>
                    {t.lines?.length > 0 && (
                      <ul className="mt-0.5 pl-2 border-l border-slate-200 space-y-0.5">
                        {t.lines.map((l) => <li key={l.name} className="text-[11px] text-slate-500 flex justify-between gap-2"><span>{l.name}</span><span className="font-mono text-slate-700 whitespace-nowrap">{l.text}</span></li>)}
                      </ul>
                    )}
                  </div>
                ))}
                <div className="flex items-center justify-between border-t border-slate-200 pt-1.5 text-sm">
                  <span className="font-semibold">Total covered</span>
                  <span className="font-mono font-semibold">{fmtA(totals.reduce((s, t) => s + t.a, 0))}</span>
                </div>
                <p className="text-[10px] text-slate-400">Estimates only — confirm on site.</p>
              </div>
            )}
          </div>
          {mine.length > 0 && (
            <div className="tm-card p-3" data-testid="plan-shared-list">
              <p className="text-xs uppercase tracking-widest text-slate-500 mb-2 font-semibold">Your shared plans</p>
              <div className="space-y-2">
                {mine.map((pl) => {
                  const link = `${window.location.origin}/p/${pl.token}`;
                  const last = pl.last_viewed_at ? new Date(pl.last_viewed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : null;
                  return (
                    <div key={pl.id} className="border border-slate-200 p-2 text-xs" data-testid={`plan-shared-${pl.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className={`inline-flex items-center gap-1 font-bold ${pl.views > 0 ? 'text-emerald-600' : 'text-slate-400'}`} data-testid={`plan-views-${pl.id}`}>
                          <Eye size={12} /> {pl.views > 0 ? `${pl.views} view${pl.views > 1 ? 's' : ''}` : 'Not viewed yet'}
                        </span>
                        <div className="flex items-center gap-1.5">
                          <button onClick={() => { navigator.clipboard.writeText(link); toast.success('Copied'); }} title="Copy link" className="text-slate-500 hover:text-slate-900"><Copy size={13} /></button>
                          <a href={link} target="_blank" rel="noopener noreferrer" title="Open" className="text-slate-500 hover:text-brand"><ExternalLink size={13} /></a>
                          <button onClick={async () => { await api.delete(`/plans/${pl.id}`); loadMine(); toast.success('Link removed'); }} title="Delete" data-testid={`plan-shared-delete-${pl.id}`} className="text-rose-500 hover:text-rose-700"><Trash2 size={13} /></button>
                        </div>
                      </div>
                      {last && <p className="text-slate-400 mt-1">Last opened {last}</p>}
                      {pl.accepted_at && <p className="text-emerald-600 font-bold mt-1 inline-flex items-center gap-1" data-testid={`plan-accepted-${pl.id}`}><CheckCircle2 size={12} /> Accepted{pl.accepted_name ? ` by ${pl.accepted_name}` : ''}</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <p className="text-[11px] text-slate-400">Tip: drag to move, corner handle to resize, top handle to rotate. Select an area to add a cobble or kerb border. Quantities update live.</p>
        </div>
      </div>
    </div>
  );
}
